import { get } from "lodash";
import { useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { formatError } from "helpers/formatError";
import { invalidateCustomEmailDomainsTableQuery } from "routes/custom_email_domains/CustomEmailDomainsTableV3";
import { getCustomEmailDomainsTableColumns } from "routes/custom_email_domains/components/CustomEmailDomainsTableColumnsAndFilters";
import CustomEmailDomainsService from "services/CustomEmailDomains";
import UserService from "services/User";
import { addResizedColumns } from "./useTableConfig";

// `create` and `update` have no error toast: BaseForm already shows a failed
// submit in the form's error banner.
export const useCustomEmailDomainMutations = ({ onDeleted } = {}) => {
  const create = useMutation({
    mutationFn: data => CustomEmailDomainsService.addDomain(data),
    onSuccess: () => {
      invalidateCustomEmailDomainsTableQuery();
      toast.success("Custom email domain created");
    },
  });

  const update = useMutation({
    mutationFn: ({ id, data }) =>
      CustomEmailDomainsService.updateDomain(id, data),
    onSuccess: () => {
      invalidateCustomEmailDomainsTableQuery();
      toast.success("Custom email domain updated");
    },
  });

  const remove = useMutation({
    mutationFn: id => CustomEmailDomainsService.deleteDomain(id),
    onSuccess: () => {
      invalidateCustomEmailDomainsTableQuery();
      toast.success("Custom email domain deleted");
      onDeleted?.();
    },
    onError: err =>
      toast.error(formatError(err, "Failed to delete custom email domain")),
  });

  return { create, update, remove };
};

export const useCustomEmailDomainsTable = ({
  tableName = "",
  handleSetAsDefault = () => {},
}) => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getCustomEmailDomainsTableColumns({
      handleSetAsDefault,
    });

    const customEmailDomainsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${tableName}`,
      {}
    );
    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: customEmailDomainsTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [tableName, tableSettings, isTableSettingsFetched, handleSetAsDefault]);

  return { columns };
};
